import { useState, useEffect } from "react";
import { supabase } from "../../Supabase/SupabaseClient";
import './Adicional.css';

export default function Adicional() {
  const [adicionales, setAdicionales] = useState([]);
  const [selectedAdicionales, setSelectedAdicionales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addedToCartIds, setAddedToCartIds] = useState(new Set());
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    async function fetchAdicionales() {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from("adicional")
          .select("*")
          .order("id, nombre, precio, descripcion");

        if (error) {
          throw error;
        }

        setAdicionales(data || []);
      } catch (error) {
        console.error("Error al cargar adicionales:", error);
        setError("No se pudieron cargar los adicionales. Por favor, intenta más tarde.");
      } finally {
        setLoading(false);
      }
    }

    fetchAdicionales();
  }, []);

  const showNotice = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 4000);
  };

  const handleAdicionalesChange = (adicionalId) => {
    setSelectedAdicionales(prev => {
      if (prev.includes(adicionalId)) {
        return prev.filter(id => id !== adicionalId);
      } else {
        return [...prev, adicionalId];
      }
    });
  };

  const calcularTotal = () => {
    return selectedAdicionales.reduce((total, id) => {
      const adicional = adicionales.find(item => item.id === id);
      return total + (adicional?.precio || 0);
    }, 0);
  };

  const handleAddToCart = async () => {
    try {
      if (selectedAdicionales.length === 0) {
        showNotice("error", "Por favor, selecciona al menos un adicional");
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        showNotice("error", "Por favor, inicia sesión primero");
        return;
      }

      for (const adicionalId of selectedAdicionales) {
        const adicional = adicionales.find(item => item.id === adicionalId);
        if (!adicional) continue;

        const { data: existingCartItems, error: checkError } = await supabase
          .from('carrito')
          .select('*')
          .eq('usuario_id', user.id)
          .eq('adicional_id', adicionalId);

        if (checkError) throw checkError;

        if (existingCartItems && existingCartItems.length > 0) {
          const existingItem = existingCartItems[0];
          const { error: updateError } = await supabase
            .from('carrito')
            .update({ cantidad: existingItem.cantidad + 1 })
            .eq('id', existingItem.id);

          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from('carrito')
            .insert({
              usuario_id: user.id,
              adicional_id: adicionalId,
              cantidad: 1
            });

          if (insertError) {
            console.error('Error de inserción:', insertError);
            throw insertError;
          }
        }
      }

      setAddedToCartIds(new Set([...addedToCartIds, ...selectedAdicionales]));

      dispatchCartUpdateEvent();

      showNotice("success", `Adicionales añadidos al carrito (${selectedAdicionales.length} item${selectedAdicionales.length === 1 ? '' : 's'})`);
      setSelectedAdicionales([]);

    } catch (err) {
      console.error('Error al añadir al carrito:', err);
      showNotice("error", 'No se pudieron añadir los adicionales al carrito: ' + err.message);
    }
  };

  const dispatchCartUpdateEvent = () => {
    const cartEvent = new CustomEvent('cartUpdate', {
      detail: { showModal: true }
    });
    document.dispatchEvent(cartEvent);
  };

  if (loading) {
    return (
      <div className="adicionales-container adicionales-container--state">
        <div className="adicionales-skeleton">
          <div className="skeleton-line short"></div>
          <div className="skeleton-line long"></div>
          <div className="skeleton-line long"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="adicionales-container adicionales-container--state">
        <div className="adicionales-message adicionales-message--error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="adicionales-container">
      <h2 className="adicionales-title">Agrega Adicionales</h2>

      {notice && (
        <div className={`adicionales-message adicionales-message--${notice.type}`} role="status">
          <p>{notice.text}</p>
        </div>
      )}

      {adicionales.length === 0 ? (
        <p className="adicionales-empty">No hay adicionales disponibles</p>
      ) : (
        <>
          <div className="adicionales-list">
            {adicionales.map((adicional) => {
              const isSelected = selectedAdicionales.includes(adicional.id);
              const alreadyAdded = addedToCartIds.has(adicional.id);
              return (
                <div
                  key={adicional.id}
                  className={`adicional-item ${isSelected ? 'adicional-item--selected' : ''} ${
                    alreadyAdded ? 'adicionado' : ''
                  }`}
                >
                  <label
                    htmlFor={`adicional-${adicional.id}`}
                    className="adicional-label"
                  >
                    <input
                      type="checkbox"
                      id={`adicional-${adicional.id}`}
                      checked={isSelected}
                      onChange={() => handleAdicionalesChange(adicional.id)}
                      className="adicional-checkbox"
                    />
                    <span className="adicional-left">
                      <span className="adicional-name">
                        {adicional.nombre}
                        {alreadyAdded && <em className="adicional-added-tag">en carrito</em>}
                      </span>
                      <span className="adicional-description">{adicional.descripcion}</span>
                    </span>
                  </label>
                  <div className="adicional-right">
                    <span className="adicional-price">${adicional.precio?.toFixed(2)}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="adicionales-total">
            <span className="adicionales-total-label">Total seleccionado:</span>
            <span className="adicionales-total-value">${calcularTotal().toFixed(2)}</span>
          </div>

          <button
            onClick={handleAddToCart}
            className={`btn primary adicionales-btn ${
              selectedAdicionales.length === 0 ? 'is-disabled' : ''
            }`}
            disabled={selectedAdicionales.length === 0}
          >
            {selectedAdicionales.length === 0
              ? "Selecciona al menos un adicional"
              : `Añadir ${selectedAdicionales.length} adicional${selectedAdicionales.length === 1 ? '' : 'es'} al carrito`}
          </button>
        </>
      )}
    </div>
  );
}